import session from './session'

/**
 * Integración SSO vía postMessage.
 *
 * La aplicación de origen ya ha obtenido el par access_token / refresh_token
 * (petición a /oauth/token) y abre esta aplicación en una pestaña nueva. Para
 * evitar que el mensaje se pierda, esta aplicación avisa al opener de que está
 * lista y el opener responde con el token.
 *
 * Protocolo:
 *  - Esta app:      window.opener.postMessage({ type: 'EFACTURA_SSO_READY' }, '*')
 *  - App de origen: e.source.postMessage({ access_token, refresh_token }, e.origin)
 */
const READY_TYPE = 'EFACTURA_SSO_READY'
const TOKEN_TIMEOUT_MS = 10000

let pending = false
let resolveToken
let rejectToken
let timeoutId

/**
 * Avisa al opener de que la aplicación está lista para recibir el token SSO.
 * Solo se envía cuando la app se abre en una pestaña nueva (hay opener), no
 * existe sesión previa y no es una sesión de soporte.
 */
export const notifyReady = () => {
  if (!window.opener || session.getToken() || session.isSupportSession()) return
  pending = true
  window.opener.postMessage({ type: READY_TYPE }, '*')
  timeoutId = setTimeout(() => {
    if (!pending) return
    pending = false
    if (rejectToken) rejectToken(new Error('SSO timeout'))
  }, TOKEN_TIMEOUT_MS)
}

/**
 * Procesa un mensaje postMessage entrante. Si trae un access_token guarda la
 * sesión y resuelve la espera del guard de autenticación.
 * @param {*} evt Evento message
 * @return {Boolean} true si el mensaje era un token SSO, false en otro caso
 */
export const handleMessage = (evt) => {
  const data = evt.data || {}
  if (!data.access_token) return false
  pending = false
  if (timeoutId) clearTimeout(timeoutId)
  session.saveNew(data)
  if (resolveToken) resolveToken(data)
  return true
}

/**
 * Devuelve si se está esperando un token SSO por postMessage
 * @return {Boolean} true si se está a la espera, false en otro caso
 */
export const isPending = () => pending

/**
 * Promesa que se resuelve cuando llega el token SSO. Se usa en el guard de
 * autenticación para no redirigir al login mientras se espera el token.
 * @return {Promise} Resuelve con los datos del token o rechaza por timeout
 */
export const waitForToken = () => {
  if (!pending) return Promise.reject(new Error('SSO not requested'))
  return new Promise((resolve, reject) => {
    resolveToken = resolve
    rejectToken = reject
  })
}

export default {
  notifyReady,
  handleMessage,
  isPending,
  waitForToken
}
